import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import {
  BroadcastScheduleDto,
  BroadcastScheduleService,
} from '@millionshow/api-client';
import { Icon } from '@millionshow/client-libs';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { NotFoundComponent } from '../../../../components/not-found/not-found.component';
import {
  formatTime,
  toDate,
  formatSingleDate,
} from '../../../../utils/date-formatter';
import { BROADCAST_STATUS_LABELS } from '@millionshow/common';
import { rxResource } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-broadcast-schedule-calendar',
  imports: [CommonModule, Icon, NotFoundComponent],
  templateUrl: './broadcast-schedule-calendar.component.html',
  styleUrls: ['./broadcast-schedule-calendar.component.css'],
})
export default class BroadcastScheduleCalendarComponent {
  private readonly broadcastScheduleService = inject<BroadcastScheduleService>(
    BroadcastScheduleService
  );

  // 현재 연도
  currentYear = signal<number>(new Date().getFullYear());

  // 검색 쿼리
  searchQuery = signal<string>('');

  // 검색 debounce
  private searchSubject = new Subject<string>();

  constructor() {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(query => {
        this.searchQuery.set(query);
      });
  }

  formatTime = formatTime;
  toDate = toDate;
  formatSingleDate = formatSingleDate;

  // API로부터 방송 스케줄 데이터 조회
  $data = rxResource({
    stream: () =>
      this.broadcastScheduleService.broadcastScheduleControllerFindAllV1(),
  });

  schedules = computed(() => this.$data.value() || []);

  // 필터링된 방송 스케줄 목록
  filteredSchedules = computed(() => {
    const schedules = this.schedules();
    const year = this.currentYear();
    const query = this.searchQuery().toLowerCase();

    return schedules
      .filter(schedule => {
        // 연도 필터 (촬영일시 기준)
        const scheduleYear = new Date(schedule.shootingAt).getFullYear();
        if (scheduleYear !== year) return false;

        // 검색 필터
        if (query) {
          const programMatch = schedule.programName
            .toLowerCase()
            .includes(query);
          const broadcasterMatch = schedule.broadcaster
            .toLowerCase()
            .includes(query);
          const eventMatch = schedule.event?.title
            .toLowerCase()
            .includes(query);
          return programMatch || broadcasterMatch || eventMatch;
        }

        return true;
      })
      .sort(
        (a, b) =>
          new Date(b.shootingAt).getTime() - new Date(a.shootingAt).getTime()
      );
  });

  getStatusBadge(status: BroadcastScheduleDto['status']) {
    const badges = {
      SCHEDULED: {
        text: BROADCAST_STATUS_LABELS['SCHEDULED'],
        class: 'bg-gray-100 text-gray-600',
      },
      SHOOTING_DONE: {
        text: BROADCAST_STATUS_LABELS['SHOOTING_DONE'],
        class: 'bg-blue-100 text-blue-600',
      },
      AIRED: {
        text: BROADCAST_STATUS_LABELS['AIRED'],
        class: 'bg-green-100 text-green-600',
      },
      CANCELLED: {
        text: BROADCAST_STATUS_LABELS['CANCELLED'],
        class: 'bg-red-100 text-red-600',
      },
    };

    return (
      badges[status] || {
        text: '알 수 없음',
        class: 'bg-gray-100 text-gray-600',
      }
    );
  }

  prevYear() {
    this.currentYear.set(this.currentYear() - 1);
  }

  nextYear() {
    this.currentYear.set(this.currentYear() + 1);
  }

  onSearchInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }
}
